import { app } from '../../app';
import { CollideEvent, CollideId, isPowerCollider } from '../../defs/collide';
import {
  CandyBagBlockEntity,
  despawnBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { CandyBagEffect } from '../effects/CandyBagEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    candy bag block collision resolver
*/
export class CandyBagCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: CandyBagBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: CandyBagBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap() {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    const collider = event.collider;

    // Only attack if is a power collision (from power blocks or boosters)
    if (isPowerCollider(collider?.type)) {
      await this._actionAttack();
      return true;
    }

    return false;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _actionAttack() {
    const position = this._entity.c.position.mapPosition;

    // play sound
    app.sound.play('puzzle-candy-bag-destroy', {
      dupes: 2,
      pan: mapGetPan(position),
    });

    // destroy the candy bag
    despawnBlockEntity(this._scene, this._entity);

    // execute candy bag effect
    return new CandyBagEffect(this._scene, {
      position,
    }).execute();
  }
}
